import { forwardRef, type ButtonHTMLAttributes, type ComponentType } from 'react';
import type { LucideProps } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { Spinner } from '../Spinner/Spinner';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'icon';
export type ButtonSize = 'sm' | 'md' | 'lg';

type IconType = ComponentType<LucideProps>;

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: IconType;
  type?: 'button' | 'submit' | 'reset';
  /** Visually-hidden label required when `variant === 'icon'` */
  'aria-label'?: string;
}

const ICON_SIZE_BY_BUTTON_SIZE: Record<ButtonSize, number> = {
  sm: 14,
  md: 16,
  lg: 18,
};

const ICON_VARIANT_SIZE_CLASS: Record<ButtonSize, string> = {
  sm: styles.iconSm ?? '',
  md: styles.iconMd ?? '',
  lg: styles.iconLg ?? '',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    icon: Icon,
    type = 'button',
    className,
    children,
    onClick,
    ...rest
  },
  ref,
) {
  const isIconOnly = variant === 'icon';
  const iconSize = ICON_SIZE_BY_BUTTON_SIZE[size];

  const sizeClass = isIconOnly ? ICON_VARIANT_SIZE_CLASS[size] : styles[size];

  return (
    <button
      ref={ref}
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(styles.button, styles[variant], sizeClass, className)}
      {...rest}
    >
      {loading ? <Spinner size="sm" label="" /> : Icon ? <Icon size={iconSize} aria-hidden /> : null}
      {!isIconOnly ? children : null}
    </button>
  );
});
